/**
 * My Templates — the account's saved badge designs. Every badge a user creates
 * is stored as a reusable template (see BadgesPage save). From here they can
 * start a new badge from a saved design, or delete one.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTemplates, useDeleteTemplate } from '../hooks/useApi';
import { api } from '../api/client';
import { PENDING_DESIGN_KEY } from './PublicDesigner';
import { ConfirmModal } from '../components/ConfirmModal';

export function MyTemplatesPage() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { data, isLoading } = useTemplates();
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const templates = data?.items ?? [];

  // Start a new badge from a saved template: fetch its design, stash it, and let
  // the badges page open the create editor pre-loaded (same handoff as /designer).
  const useTemplate = async (id: string) => {
    setBusyId(id);
    try {
      const tpl = await api.getTemplate(id);
      if (tpl.designJson) {
        localStorage.setItem(PENDING_DESIGN_KEY, JSON.stringify({ designJson: tpl.designJson, image: tpl.image ?? undefined }));
        navigate('/badges');
        return;
      }
    } catch { /* fall through */ }
    setBusyId(null);
  };

  return (
    <div className="page-wide px-4 py-8 sm:px-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('myTemplates.title', 'My Templates')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('myTemplates.subtitle', 'Your saved badge designs. Start a new badge from any of them.')}</p>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-gray-400">{t('common:loading', 'Loading…')}</div>
      ) : templates.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 py-16 text-center">
          <p className="text-sm text-gray-500">{t('myTemplates.empty', 'No saved templates yet — every badge you create is saved here automatically.')}</p>
          <button onClick={() => navigate('/badges')} className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
            {t('myTemplates.createFirst', 'Create a badge')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {templates.map((tpl) => (
            <div key={tpl.id} data-testid={`my-template-${tpl.id}`} className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex aspect-square items-center justify-center bg-gray-50 p-3">
                {tpl.image ? <img src={tpl.image} alt={tpl.name} className="max-h-full max-w-full object-contain" loading="lazy" /> : <span className="text-xs text-gray-300">{t('myTemplates.noPreview', 'No preview')}</span>}
              </div>
              <div className="flex flex-1 flex-col p-3">
                <div className="truncate text-sm font-semibold text-gray-800" title={tpl.name}>{tpl.name}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => useTemplate(tpl.id)} disabled={busyId === tpl.id} className="flex-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
                    {busyId === tpl.id ? t('myTemplates.opening', 'Opening…') : t('myTemplates.use', 'Use')}
                  </button>
                  <button onClick={() => setConfirmId(tpl.id)} className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50" title={t('myTemplates.delete', 'Delete')}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!confirmId}
        title={t('myTemplates.deleteTitle', 'Delete template?')}
        message={t('myTemplates.deleteMessage', 'This saved design will be removed. Badges already created from it are not affected.')}
        confirmLabel={t('myTemplates.delete', 'Delete')}
        onConfirm={() => { if (confirmId) deleteTemplate(confirmId); setConfirmId(null); }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}
